import { Loader2, CheckCircle2, XCircle, FileCheck } from "lucide-react"
import { Badge } from "./badge"
import { CVDocument } from "@/hooks/use-cv-upload"
import { formatCvParsingErrorMessage } from "@/lib/cv-parsing-error"
import { formatCvRelativeDate } from "@/lib/cv-display"
import { cn } from "@/lib/utils"

interface CVParsingStatusProps {
  cvDocument: CVDocument | null
  className?: string
  /** Completed state tuned for onboarding upload summary (filename + date). */
  completedPresentation?: "default" | "file-summary"
}

export function CVParsingStatus({
  cvDocument,
  className,
  completedPresentation = "default",
}: CVParsingStatusProps) {
  if (!cvDocument) {
    return null
  }

  const { parsing_status, parsing_error } = cvDocument

  const statusConfig = {
    pending: {
      icon: Loader2,
      label: "Processing your CV...",
      description: "Your CV is queued for processing",
      variant: "secondary" as const,
      animate: true,
    },
    processing: {
      icon: Loader2,
      label: "Extracting information...",
      description: "We're analyzing your CV and extracting your profile details",
      variant: "secondary" as const,
      animate: true,
    },
    completed: {
      icon: completedPresentation === "file-summary" ? FileCheck : CheckCircle2,
      label:
        completedPresentation === "file-summary"
          ? "CV ready"
          : "CV processed successfully",
      description:
        completedPresentation === "file-summary"
          ? null
          : "Your profile has been updated with information from your CV",
      variant: "successSoft" as const,
      animate: false,
    },
    failed: {
      icon: XCircle,
      label: "Processing failed",
      description: formatCvParsingErrorMessage(parsing_error),
      variant: "destructiveSoft" as const,
      animate: false,
    },
  }

  const config = statusConfig[parsing_status] || statusConfig.pending
  const Icon = config.icon
  const isCompletedSummary =
    parsing_status === "completed" && completedPresentation === "file-summary"

  return (
    <div
      role={parsing_status === "failed" ? "alert" : "status"}
      aria-live={parsing_status === "failed" ? undefined : "polite"}
      className={cn(
        "flex items-start gap-3 rounded-lg border p-4",
        isCompletedSummary && "border-success/30 bg-success/10",
        className,
      )}
    >
      <div
        aria-hidden
        className={cn("shrink-0", config.animate && "animate-spin")}
      >
        <Icon
          className={cn(
            "h-5 w-5",
            parsing_status === "completed" && "text-success",
            parsing_status === "failed" && "text-destructive",
            (parsing_status === "pending" || parsing_status === "processing") &&
              "text-muted-foreground",
          )}
        />
      </div>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium">{config.label}</p>
          {!isCompletedSummary ? (
            <Badge variant={config.variant} className="text-xs">
              {parsing_status}
            </Badge>
          ) : null}
        </div>
        {isCompletedSummary && cvDocument.original_filename ? (
          <p
            className="truncate text-sm text-foreground"
            title={cvDocument.original_filename}
          >
            {cvDocument.original_filename}
          </p>
        ) : null}
        {config.description ? (
          <p className="text-xs text-muted-foreground">{config.description}</p>
        ) : null}
        {isCompletedSummary && cvDocument.uploaded_at ? (
          <p className="text-xs text-muted-foreground">
            Uploaded {formatCvRelativeDate(cvDocument.uploaded_at)}
          </p>
        ) : null}
        {parsing_status === "failed" && cvDocument.original_filename ? (
          <p className="truncate text-xs text-muted-foreground" title={cvDocument.original_filename}>
            File: {cvDocument.original_filename}
          </p>
        ) : null}
      </div>
    </div>
  )
}
